class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;
    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());

    // Handle sticky toolbar on mobile
    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    // TODO: get from URL Search Params
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    const resume = this.sidebarResume || { open: this.sidebarOpen, focusId: this.sidebarFocusId };
    this.destroySidebarFilter();
    this.sidebarResume = null;
    if (!this.enableFilters) return;
    const { sidebar, sidebarContent, openSidebar, closeSidebar, section } = this.domNodes;
    if (!sidebar || !sidebarContent || !openSidebar || !closeSidebar || !section) return;
    const nodes = { sidebar, sidebarContent, openSidebar, closeSidebar, section };
    this.sidebarNodes = nodes;
    this.sidebarBreakpoint = window.matchMedia("(max-width: 1279px)");
    sidebarContent.id = sidebarContent.id || `CollectionFiltersDialog-${this.sectionId}`;
    sidebarContent.setAttribute("tabindex", "-1");
    openSidebar.setAttribute("aria-controls", sidebarContent.id);
    openSidebar.setAttribute("aria-expanded", "false");
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", this.onSidebarOverlayClick);
    sidebarContent.addEventListener("focusin", this.onSidebarFocus);
    document.addEventListener("keydown", this.onSidebarKeydown);
    this.sidebarBreakpoint.addEventListener("change", this.syncSidebarLayout);
    this.sidebarLifecycleObserver = new MutationObserver(() => {
      if (this.sidebarNodes === nodes && !section.isConnected) {
        const state = { open: this.sidebarOpen, focusId: this.sidebarFocusId };
        this.destroySidebarFilter();
        this.sidebarResume = state;
      }
    });
    this.sidebarLifecycleObserver.observe(document.body, { childList: true, subtree: true });
    this.syncSidebarLayout();
    this.sidebarInitFrame = window.requestAnimationFrame(() => {
      if (this.sidebarNodes !== nodes || !section.isConnected) return;
      this.initAccordions();
      if (resume.open && this.isSidebarOffCanvas()) this.openSidebarFilter(null, resume.focusId);
    });
  };

  isSidebarOffCanvas = () => Boolean(this.sidebarNodes && (this.sidebarBreakpoint.matches || this.sidebarNodes.sidebar.dataset.type === "fixed"));

  onSidebarOverlayClick = (event) => {
    if (event.target === this.sidebarNodes?.sidebar) this.closeSidebarFilter();
  };

  onSidebarFocus = (event) => { this.sidebarFocusId = event.target.id || ""; };

  getSidebarFocusable = () => getFocusableElements(this.sidebarNodes.sidebarContent).filter((node) =>
    !node.closest("[inert], [aria-hidden='true']") && node.getClientRects().length && getComputedStyle(node).visibility !== "hidden"
  );

  onSidebarKeydown = (event) => {
    if (!this.sidebarOpen || !this.sidebarNodes?.section.isConnected || !this.isSidebarOffCanvas()) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.closeSidebarFilter();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = this.getSidebarFocusable();
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!first) {
      event.preventDefault();
      this.sidebarNodes.sidebarContent.focus({ preventScroll: true });
    } else if (!focusable.includes(active) || (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    }
  };

  syncSidebarLayout = () => {
    const nodes = this.sidebarNodes;
    if (!nodes?.section.isConnected) return;
    if (!this.isSidebarOffCanvas()) {
      this.closeSidebarFilter({ restoreFocus: false, immediate: true });
    } else if (!this.sidebarOpen) {
      if (nodes.sidebarContent.contains(document.activeElement)) this.openSidebarFilter();
      else {
        nodes.sidebarContent.inert = true;
        nodes.sidebarContent.setAttribute("aria-hidden", "true");
      }
    }
  };

  destroySidebarFilter = () => {
    const nodes = this.sidebarNodes;
    clearTimeout(this.sidebarCloseTimer);
    window.cancelAnimationFrame(this.sidebarOpenFrame);
    window.cancelAnimationFrame(this.sidebarInitFrame);
    this.sidebarLifecycleObserver?.disconnect();
    this.sidebarBreakpoint?.removeEventListener("change", this.syncSidebarLayout);
    document.removeEventListener("keydown", this.onSidebarKeydown);
    if (nodes) {
      nodes.openSidebar.removeEventListener("click", this.openSidebarFilter);
      nodes.closeSidebar.removeEventListener("click", this.closeSidebarFilter);
      nodes.sidebar.removeEventListener("click", this.onSidebarOverlayClick);
      nodes.sidebarContent.removeEventListener("focusin", this.onSidebarFocus);
      nodes.openSidebar.setAttribute("aria-expanded", "false");
      nodes.sidebarContent.inert = false;
      ["aria-hidden", "aria-modal", "role", "aria-label"].forEach((name) => nodes.sidebarContent.removeAttribute(name));
      nodes.sidebar.style.removeProperty("display");
      nodes.sidebar.style.removeProperty("--m-bg-opacity");
      nodes.sidebarContent.style.removeProperty("--m-translate-x");
      nodes.section.classList.remove("sidebar-open");
      this.accordions?.destroy();
      this.accordions = null;
    }
    this.sidebarOpen = false;
    this.sidebarNodes = null;
    this.sidebarReturnFocus = null;
  };

  initMobileSorting = () => {
    const resume = this.sortingResume || { open: this.sortingOpen, index: this.sortingFocusIndex, restoreOpener: this.sortingRestoreAfterRender };
    this.destroyMobileSorting();
    this.sortingResume = null;
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper, sortingList } = this.mbSortingNodes;
    const section = this.domNodes.section;
    if (!openSorting || !closeSorting || !sortingWrapper || !sortingList || !section) return;
    const nodes = { openSorting, closeSorting, sortingWrapper, sortingList, section };
    this.sortingNodes = nodes;
    this.sortingBreakpoint = window.matchMedia("(max-width: 767px)");
    sortingList.id = sortingList.id || `CollectionSortingDialog-${this.sectionId}`;
    sortingList.setAttribute("tabindex", "-1");
    sortingList.inert = true;
    sortingList.setAttribute("aria-hidden", "true");
    openSorting.setAttribute("aria-controls", sortingList.id);
    openSorting.setAttribute("aria-expanded", "false");
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.onSortingCloseClick);
    sortingWrapper.addEventListener("click", this.onSortingOverlayClick);
    sortingList.addEventListener("focusin", this.onSortingFocus);
    this.sortingBreakpoint.addEventListener("change", this.onSortingBreakpoint);
    this.sortingLifecycleObserver = new MutationObserver(() => {
      if (this.sortingNodes === nodes && !section.isConnected) {
        const state = { open: this.sortingOpen, index: this.sortingFocusIndex, restoreOpener: this.sortingRestoreAfterRender };
        this.destroyMobileSorting();
        this.sortingResume = state;
      }
    });
    this.sortingLifecycleObserver.observe(document.body, { childList: true, subtree: true });
    if (this.sortingBreakpoint.matches) {
      if (resume.open) this.openMobileSorting(null, resume.index);
      else if (resume.restoreOpener && document.activeElement === document.body) openSorting.focus({ preventScroll: true });
      else if (resume.restoreOpener && document.activeElement === openSorting) {
        this.sortingRestoreAfterRender = true;
        document.addEventListener("focusin", this.onSortingPendingFocus);
      }
    }
  };

  getSortingFocusable = () => getFocusableElements(this.sortingNodes.sortingList).filter((node) =>
    !node.closest("[inert], [aria-hidden='true']") && node.getClientRects().length && getComputedStyle(node).visibility !== "hidden"
  );

  onSortingOverlayClick = (event) => { if (event.target === this.sortingNodes?.sortingWrapper) this.closeMobileSorting({ selectedOption: false }); };

  onSortingCloseClick = () => this.closeMobileSorting({ selectedOption: false });

  onSortingFocus = (event) => { this.sortingFocusIndex = event.target.closest(".m-sortby-mobile--item")?.dataset.index; };

  onSortingPendingFocus = (event) => {
    if (!this.sortingNodes?.section.isConnected && event.target === document.body) return;
    if (event.target !== this.sortingNodes?.openSorting) {
      this.sortingRestoreAfterRender = false;
      document.removeEventListener("focusin", this.onSortingPendingFocus);
    }
  };

  onSortingKeydown = (event) => {
    if (!this.sortingOpen || !this.sortingNodes?.section.isConnected) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.closeMobileSorting({ selectedOption: false });
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = this.getSortingFocusable();
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
      this.sortingNodes.sortingList.focus({ preventScroll: true });
    } else if (!focusable.includes(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    }
  };

  onSortingBreakpoint = () => {
    if (!this.sortingBreakpoint.matches) {
      const target = this.domNodes.toolbar?.querySelector("[data-toolbar-sorting] select");
      this.closeMobileSorting({ immediate: true, returnTarget: target, selectedOption: false });
    }
  };

  destroyMobileSorting = () => {
    const nodes = this.sortingNodes;
    clearTimeout(this.sortingCloseTimer);
    window.cancelAnimationFrame(this.sortingOpenFrame);
    this.sortingLifecycleObserver?.disconnect();
    this.sortingBreakpoint?.removeEventListener("change", this.onSortingBreakpoint);
    document.removeEventListener("keydown", this.onSortingKeydown);
    document.removeEventListener("focusin", this.onSortingPendingFocus);
    if (nodes) {
      nodes.openSorting.removeEventListener("click", this.openMobileSorting);
      nodes.closeSorting.removeEventListener("click", this.onSortingCloseClick);
      nodes.sortingWrapper.removeEventListener("click", this.onSortingOverlayClick);
      nodes.sortingList.removeEventListener("focusin", this.onSortingFocus);
      nodes.openSorting.setAttribute("aria-expanded", "false");
      nodes.sortingList.inert = true;
      nodes.sortingList.setAttribute("aria-hidden", "true");
      ["role", "aria-modal", "aria-label"].forEach((name) => nodes.sortingList.removeAttribute(name));
      nodes.sortingWrapper.style.removeProperty("display");
      nodes.sortingWrapper.style.removeProperty("--m-bg-opacity");
      nodes.sortingList.style.removeProperty("--m-translate-y");
    }
    this.sortingOpen = false;
    this.sortingRestoreAfterRender = false;
    this.sortingNodes = null;
    this.sortingReturnFocus = null;
  };

  openMobileSorting = (event, preferredIndex) => {
    const nodes = this.sortingNodes;
    if (!nodes?.section.isConnected || !this.sortingBreakpoint.matches) return;
    clearTimeout(this.sortingCloseTimer);
    window.cancelAnimationFrame(this.sortingOpenFrame);
    this.sortingRestoreAfterRender = false;
    document.removeEventListener("focusin", this.onSortingPendingFocus);
    this.sortingOpen = true;
    this.sortingReturnFocus = event?.currentTarget || nodes.openSorting;
    nodes.sortingList.inert = false;
    nodes.sortingList.removeAttribute("aria-hidden");
    nodes.sortingList.setAttribute("role", "dialog");
    nodes.sortingList.setAttribute("aria-modal", "true");
    nodes.sortingList.setAttribute("aria-label", nodes.sortingList.querySelector(".m-sortby-mobile--title").textContent.trim());
    nodes.openSorting.setAttribute("aria-expanded", "true");
    nodes.sortingWrapper.style.display = "block";
    document.addEventListener("keydown", this.onSortingKeydown);
    this.sortingOpenFrame = window.requestAnimationFrame(() => {
      if (this.sortingNodes !== nodes || !this.sortingOpen || !nodes.section.isConnected) return;
      nodes.sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      nodes.sortingList.style.setProperty("--m-translate-y", "0");
      const focusable = this.getSortingFocusable();
      const previous = focusable.find((node) => node.closest(".m-sortby-mobile--item")?.dataset.index === preferredIndex);
      (previous || (focusable.includes(nodes.closeSorting) ? nodes.closeSorting : focusable[0]) || nodes.sortingList).focus({ preventScroll: true });
    });
  };

  closeMobileSorting = ({ restoreFocus = true, immediate = false, returnTarget = null, selectedOption } = {}) => {
    const nodes = this.sortingNodes;
    if (!nodes) return;
    const wasOpen = this.sortingOpen;
    const selectedFromDialog = wasOpen && selectedOption !== false && nodes.sortingList.contains(document.activeElement) && document.activeElement.closest(".m-sortby-mobile--item");
    clearTimeout(this.sortingCloseTimer);
    window.cancelAnimationFrame(this.sortingOpenFrame);
    this.sortingOpen = false;
    document.removeEventListener("keydown", this.onSortingKeydown);
    nodes.openSorting.setAttribute("aria-expanded", "false");
    const target = returnTarget || this.sortingReturnFocus;
    if (restoreFocus && wasOpen && target?.isConnected && target.getClientRects().length) target.focus({ preventScroll: true });
    else if (restoreFocus && wasOpen && nodes.sortingList.contains(document.activeElement)) document.activeElement.blur();
    nodes.sortingList.inert = true;
    nodes.sortingList.setAttribute("aria-hidden", "true");
    ["role", "aria-modal", "aria-label"].forEach((name) => nodes.sortingList.removeAttribute(name));
    nodes.sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    nodes.sortingList.style.setProperty("--m-translate-y", "100%");
    this.sortingRestoreAfterRender = Boolean(selectedFromDialog && !returnTarget);
    if (this.sortingRestoreAfterRender) document.addEventListener("focusin", this.onSortingPendingFocus);
    else document.removeEventListener("focusin", this.onSortingPendingFocus);
    if (immediate) nodes.sortingWrapper.style.removeProperty("display");
    else this.sortingCloseTimer = setTimeout(() => {
      if (this.sortingNodes === nodes && !this.sortingOpen && nodes.section.isConnected) nodes.sortingWrapper.style.removeProperty("display");
    }, 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
  };

  openSidebarFilter = (event, preferredFocusId = "") => {
    const nodes = this.sidebarNodes;
    if (!nodes?.section.isConnected || !this.isSidebarOffCanvas()) return;
    const { sidebar, sidebarContent, section, openSidebar, closeSidebar } = nodes;
    clearTimeout(this.sidebarCloseTimer);
    window.cancelAnimationFrame(this.sidebarOpenFrame);
    this.sidebarOpen = true;
    this.sidebarReturnFocus = event?.currentTarget || openSidebar;
    sidebarContent.inert = false;
    sidebarContent.removeAttribute("aria-hidden");
    sidebarContent.setAttribute("role", "dialog");
    sidebarContent.setAttribute("aria-modal", "true");
    sidebarContent.setAttribute("aria-label", sidebar.querySelector(".m-sidebar--title")?.textContent.trim() || openSidebar.textContent.trim());
    openSidebar.setAttribute("aria-expanded", "true");
    sidebar.style.display = "block";
    section.classList.add("sidebar-open");
    this.sidebarOpenFrame = window.requestAnimationFrame(() => {
      if (!this.sidebarOpen || this.sidebarNodes !== nodes || !section.isConnected) return;
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
      const previous = preferredFocusId && document.getElementById(preferredFocusId);
      const focusable = this.getSidebarFocusable();
      const target = previous && focusable.includes(previous) ? previous : (focusable.includes(closeSidebar) ? closeSidebar : focusable[0] || sidebarContent);
      target.focus({ preventScroll: true });
    });
  };

  closeSidebarFilter = ({ restoreFocus = true, immediate = false } = {}) => {
    const nodes = this.sidebarNodes;
    if (!nodes) return;
    const { sidebar, sidebarContent, section, openSidebar } = nodes;
    const wasOpen = this.sidebarOpen;
    clearTimeout(this.sidebarCloseTimer);
    window.cancelAnimationFrame(this.sidebarOpenFrame);
    this.sidebarOpen = false;
    section.classList.remove("sidebar-open");
    openSidebar.setAttribute("aria-expanded", "false");
    ["aria-modal", "role", "aria-label"].forEach((name) => sidebarContent.removeAttribute(name));
    if (this.isSidebarOffCanvas()) {
      // Move focus out before making the closing drawer inert.
      if (restoreFocus && wasOpen && this.sidebarReturnFocus?.isConnected) this.sidebarReturnFocus.focus({ preventScroll: true });
      sidebarContent.inert = true;
      sidebarContent.setAttribute("aria-hidden", "true");
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      if (immediate) sidebar.style.removeProperty("display");
      else this.sidebarCloseTimer = setTimeout(() => {
        if (this.sidebarNodes === nodes && !this.sidebarOpen && section.isConnected) sidebar.style.removeProperty("display");
      }, 300);
    } else {
      sidebarContent.inert = false;
      sidebarContent.removeAttribute("aria-hidden");
      sidebar.style.removeProperty("display");
      sidebar.style.removeProperty("--m-bg-opacity");
      sidebarContent.style.removeProperty("--m-translate-x");
    }
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    // Resize image product card
    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    const activeCol = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      cols[activeCol - 1].classList.remove("active");
      cols[col - 1].classList.add("active");
      cols.forEach((button, index) => button.setAttribute("aria-pressed", String(index + 1 === col)));
    }

    productContainer.classList.remove(`m-cols-${activeCol}`);
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return; // List layout
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (maxCol < this.activeCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } })
      .then((productGridHTML) => {
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));
        this.initProducts();
      })
      .catch((err) => console.error(`Failed to load more products.`, err))
      .finally(() => {
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;
        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }
      });
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    const extraSpace = toolbar && toolbar.offsetTop;
    let lastScroll = 0;
    window.addEventListener("scroll", () => {
      if (MinimogTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    });
  }
}